import { Button } from 'components/Button';
import { Input } from 'components/Shared/Form/themes/BGH-RD3/input';
import { IconMinus } from 'components/Shared/Icon/themes/BGH-RD3/iconMinus';
import { IconPlus } from 'components/Shared/Icon/themes/BGH-RD3/iconPlus';
import Modal from 'components/Shared/Modal';
import { subDays, subMonths } from 'date-fns';
import { useToggle } from 'hooks';
import { CustomerPageOrderLine } from 'models/CustomerPageOrderLine';
import { SupportProductModel } from 'models/SupportProductModel';
import { Reducer, useEffect, useReducer } from 'react';
import { formatDateShort, rm } from 'utils';
import { dateIsValid } from 'views/CustomerPage/OrderHistory/themes/BGH/dateValidator';
import { OrderHistoryDatePicker } from './OrderHistoryDatePicker';
import { DisplayMode } from './OrderLine';
import styles from './styles.scss';

const MAX_DATE_AGE = 15;

interface OwnProps {
  className?: string;
}

interface MergedProps extends OwnProps {
  line: CustomerPageOrderLine;
  deliveryDate?: string;
  isActive?: boolean;
  onChange?: (product: SupportProductModel) => void;
  displayMode: DisplayMode;
  hasTracking: boolean;
}

interface OwnState {
  maxQuantity: number;
  selectedQuantity: number;
  deliveryDate: string;
  manufactureItem: boolean;
  incementAfter: boolean;
}

const initialState: OwnState = {
  maxQuantity: 0,
  selectedQuantity: 0,
  deliveryDate: '',
  manufactureItem: false,
  incementAfter: false
};

interface InitAction {
  maxQuantity: number;
  manufactureItem: boolean;
  deliveryDate: string;
  incementAfter: boolean;
}

type Action =
  | { type: 'quantity'; value: number }
  | { type: 'init'; value: InitAction }
  | { type: 'date'; value: string }
  | { type: 'incementAfter'; value: boolean };

const ownReducer: Reducer<OwnState, Action> = (state, action) => {
  switch (action.type) {
    case 'init':
      return {
        ...state,
        maxQuantity: action.value.maxQuantity,
        manufactureItem: action.value.manufactureItem,
        deliveryDate: action.value.deliveryDate
      };
    case 'quantity':
      return {
        ...state,
        selectedQuantity: action.value
      };
    case 'date':
      return {
        ...state,
        deliveryDate: action.value
      };
    case 'incementAfter':
      return {
        ...state,
        incementAfter: action.value
      };
    default:
      return state;
  }
};

const IsQuantitySelectorEnabled = (
  manufactureItem: boolean,
  deliveryDate: string,
  isActive: boolean | undefined,
  displayMode: DisplayMode,
  hasTracking: boolean,
  isRentalProduct: boolean | undefined
) => {
  if (isRentalProduct) return false;

  if (displayMode === DisplayMode.Claims) {
    return true;
  }

  if (manufactureItem) {
    return false;
  }

  if (displayMode === DisplayMode.Change) {
    if (hasTracking) {
      return false;
    }
    return true;
  }

  return (deliveryDate === '' || dateIsValid(deliveryDate, MAX_DATE_AGE)) && isActive;
};

const quantityFocus = (
  deliveryDate: string,
  toggle: (increaseAfter: boolean) => void,
  displayMode: DisplayMode
) => {
  if (deliveryDate === '' && displayMode === DisplayMode.Returns) {
    toggle(false);
    return false;
  }
  return true;
};

const quantityInputChange = (
  e: React.ChangeEvent<HTMLInputElement>,
  maxQuantity: number,
  dispatch: (value: Action) => void,
  onChange: (quantity: number) => void
) => {
  const val = parseInt(e.target.value);
  if (!isNaN(val) && val <= maxQuantity && val >= 0) {
    dispatch({ type: 'quantity', value: val });
    onChange(val);
  }
};

const increaseQuantity = (
  deliveryDate: string,
  selectedQuantity: number,
  dispatch: (value: Action) => void,
  toggle: (increaseAfter: boolean) => void,
  onChange: (quantity: number) => void,
  displayMode: DisplayMode
) => {
  if (deliveryDate === '' && displayMode === DisplayMode.Returns) {
    toggle(true);
    return;
  }

  dispatch({ type: 'quantity', value: selectedQuantity + 1 });
  onChange(selectedQuantity + 1);
};

const decreaseQuantity = (
  selectedQuantity: number,
  dispatch: (value: Action) => void,
  onChange: (quantity: number) => void
) => {
  dispatch({ type: 'quantity', value: selectedQuantity - 1 });
  onChange(selectedQuantity - 1);
};

const getNotAvailableReason = (
  deliveryDate: string,
  isActive: boolean | undefined,
  hasTracking: boolean,
  displayMode: DisplayMode,
  isInvoiced: boolean | undefined,
  isRentalProduct: boolean | undefined
) => {
  if (isRentalProduct) {
    return rm('customerpage_returnquantity_rentalproduct');
  }

  if (displayMode === DisplayMode.Change && hasTracking) {
    return rm('customerpage_change_quantity_reason_sent');
  }

  if (displayMode === DisplayMode.Change && isInvoiced) {
    return rm('customerpage_change_quantity_reason_invoiced');
  }

  if (deliveryDate !== '') {
    return `${rm('customerpage_returnquantity_delivered')} ${deliveryDate}`;
  }

  if (isActive) {
    return rm('customerpage_returnquantity_manufactureitem');
  }

  return '';
};

export const QuantitySelector = ({
  line,
  isActive,
  deliveryDate,
  onChange,
  displayMode,
  hasTracking
}: MergedProps) => {
  const [state, dispatch] = useReducer(ownReducer, initialState);
  const [showDateSelector, toggleDateSelector] = useToggle(false);
  const todaysDate = new Date();

  useEffect(() => {
    dispatch({
      type: 'init',
      value: {
        maxQuantity: line.quantity,
        manufactureItem: line.manufactureItem as boolean,
        deliveryDate: deliveryDate ?? '',
        incementAfter: false
      }
    });
  }, [line, deliveryDate]);

  /**
   * Toggles the dateSelector and sets if an inceremnt should happen after the modal closes.
   * @param increaseAfter if the quantity should be inceremtned after a valid date is set
   */
  const toggle = (increaseAfter: boolean) => {
    toggleDateSelector();
    dispatch({
      type: 'incementAfter',
      value: increaseAfter
    });
  };

  /**
   * Callback for when a date is selected.
   * Also increases the quatity with +1 if needed
   * @param date The selected date
   */
  const dateSelected = (date: Date) => {
    const formatedDate = formatDateShort(date);
    dispatch({
      type: 'date',
      value: formatedDate
    });

    if (state.incementAfter) {
      dispatch({ type: 'quantity', value: state.selectedQuantity + 1 });
      onChangeInternal(state.selectedQuantity + 1, formatedDate);
    }

    toggle(false);
  };

  const onChangeInternal = (quantity: number, date?: string) => {
    if (onChange) {
      onChange({
        productId: line.productId,
        productName: line.variantName ? line.name + ' - ' + line.variantName : line.name,
        productRecievedDate: date === undefined ? state.deliveryDate : date,
        quantity: quantity,
        unit: line.unit
      });
    }
  };

  const quantitySelectorEnabled = IsQuantitySelectorEnabled(
    state.manufactureItem,
    state.deliveryDate,
    isActive,
    displayMode,
    hasTracking,
    line.isRentalProduct
  );

  return (
    <div className={styles.returns}>
      <Modal
        show={showDateSelector}
        onClose={toggleDateSelector}
        contentClassName={styles.returnModal}
      >
        <h2>{rm('customerpage_returnquantity_modal_header')}</h2>
        <p className={styles.body}>{rm('customerpage_returnquantity_modal_body')}</p>
        <div className={styles.blue}>
          <OrderHistoryDatePicker
            className={styles.datePicker}
            rangeStartDate={todaysDate}
            selectDate={dateSelected}
            rangeEndDate={subDays(todaysDate, 14)}
            currentMonth={subMonths(todaysDate, 1)}
          />
          <OrderHistoryDatePicker
            rangeStartDate={todaysDate}
            selectDate={dateSelected}
            rangeEndDate={subDays(todaysDate, 14)}
            currentMonth={todaysDate}
            className={styles.datePicker}
          />
        </div>
        <div className={styles.buttonContainer}>
          <Button
            appearance="whitePillPrimary"
            onClick={toggleDateSelector}
            className={styles.button}
          >
            {rm('customerpage_returnquantity_modal_confirm')}
          </Button>
          <Button appearance="whitePill" onClick={toggleDateSelector} className={styles.button}>
            {rm('customerpage_returnquantity_modal_cancel')}
          </Button>
        </div>
      </Modal>
      {quantitySelectorEnabled && (
        <>
          <Input
            type="number"
            value={state.selectedQuantity}
            unit={line.unit}
            className={styles.quantityInput}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              quantityInputChange(e, state.maxQuantity, dispatch, onChangeInternal)
            }
            onFocus={() => quantityFocus(state.deliveryDate, toggle, displayMode)}
            inputMode="numeric"
          />
          <Button
            className={styles.quantityButton}
            appearance="tertiary"
            disabled={state.selectedQuantity === 0}
            onClick={() => decreaseQuantity(state.selectedQuantity, dispatch, onChangeInternal)}
          >
            <IconMinus />
          </Button>
          <Button
            className={styles.quantityButton}
            appearance="tertiary"
            disabled={state.selectedQuantity >= state.maxQuantity}
            onClick={() =>
              increaseQuantity(
                state.deliveryDate,
                state.selectedQuantity,
                dispatch,
                toggle,
                onChangeInternal,
                displayMode
              )
            }
          >
            <IconPlus />
          </Button>
        </>
      )}
      {!quantitySelectorEnabled && (
        <div className={styles.returnDisabled}>
          <span>
            {getNotAvailableReason(
              state.deliveryDate,
              isActive,
              hasTracking,
              displayMode,
              line.isInvoiced,
              line.isRentalProduct
            )}
          </span>
          <span>
            {displayMode === DisplayMode.Returns
              ? rm('customerpage_returnquantity_no_return')
              : rm('customerpage_change_quantity_not_available')}
          </span>
          {line.isRentalProduct ? (
            <a href={rm('customerpage_returnquantity_rentalinformation_url')}>
              {rm('customerpage_returnquantity_rentalinformation')}
            </a>
          ) : (
            <a href={rm('customerpage_returnquantity_terms_url')}>
              {rm('customerpage_returnquantity_terms')}
            </a>
          )}
        </div>
      )}
    </div>
  );
};
